import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Users, Filter, X } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";

function formatDate(dateIso) {
    if (!dateIso) return "";
    const d = new Date(dateIso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function daysUntil(iso) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const t = new Date(iso);
    t.setHours(0, 0, 0, 0);
    return Math.round((t - now) / (1000 * 60 * 60 * 24));
}

function UrgencyBadge({ s, lastOfYear }) {
    const seats = s.available_seats ?? 0;
    const days = daysUntil(s.start_date);
    if (seats === 0) {
        return <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-700" data-testid={`badge-full-${s.id}`}>Complet</span>;
    }
    if (seats <= 3) {
        return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-red-700" data-testid={`badge-few-${s.id}`}>⚠ Plus que {seats} place{seats > 1 ? "s" : ""}</span>;
    }
    if (days >= 0 && days <= 30) {
        return <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-orange" data-testid={`badge-soon-${s.id}`}>🔥 Dernières inscriptions</span>;
    }
    if (lastOfYear) {
        const year = new Date(s.start_date).getFullYear();
        return <span className="inline-flex items-center rounded-full bg-brand-ink px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white" data-testid={`badge-last-year-${s.id}`}>Dernière session {year}</span>;
    }
    return null;
}

export default function Sessions() {
    const [sessions, setSessions] = useState([]);
    const [cursus, setCursus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ cursus: "", city: "" });

    useEffect(() => {
        Promise.all([api.get("/sessions"), api.get("/cursus")])
            .then(([sRes, cRes]) => {
                setSessions(sRes.data);
                setCursus(cRes.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const cities = useMemo(() => {
        return Array.from(new Set(sessions.map((s) => s.city))).sort();
    }, [sessions]);

    const filteredSessions = useMemo(() => {
        return sessions.filter((s) => {
            if (filters.cursus && s.cursus_slug !== filters.cursus) return false;
            if (filters.city && s.city !== filters.city) return false;
            return true;
        });
    }, [sessions, filters]);

    // Last session per formation per year (for "Dernière session {year}" badge)
    const lastOfYearMap = useMemo(() => {
        const acc = {};
        [...sessions]
            .sort((a, b) => a.start_date.localeCompare(b.start_date))
            .forEach((s) => {
                const key = `${s.formation_slug}|${new Date(s.start_date).getFullYear()}`;
                acc[key] = s.id;
            });
        return acc;
    }, [sessions]);

    const hasFilters = filters.cursus || filters.city;

    return (
        <div data-testid="page-sessions" className="bg-white">
            {/* Hero */}
            <section className="relative bg-brand-ink text-white pt-40 pb-24 md:pt-48 md:pb-32 grain">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange">
                            Prochaines sessions
                        </span>
                        <h1 className="mt-6 max-w-4xl font-display text-5xl font-normal leading-[1.03] tracking-tightest sm:text-6xl lg:text-7xl">
                            Trouvez la session qui vous <em className="italic text-brand-orange">correspond</em>.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
                            Retrouvez toutes les sessions à venir pour l'ensemble de nos formations.
                            Filtrez par cursus, par ville, et inscrivez-vous en quelques clics.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Bandeau 3 axes de Prévention Active */}
            <section className="border-b border-brand-line bg-[#F5F2EE] py-10" data-testid="sessions-axes-band">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="mb-6 text-center">
                        <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                            3 axes de Prévention Active
                        </span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        {[
                            { n: "01", code: "SOMA", tag: "Maîtriser le corps en mouvement." },
                            { n: "02", code: "NEURO-KINESIS", tag: "Évaluer, mesurer, décider." },
                            { n: "03", code: "VITAL", tag: "Optimiser la vie pour durer." },
                        ].map((a) => (
                            <div key={a.n} className="rounded-2xl border border-brand-ink/10 bg-white p-5" data-testid={`axe-${a.code}`}>
                                <div className="flex items-center gap-3">
                                    <span className="font-display text-2xl italic text-brand-orange">{a.n}</span>
                                    <span className="font-display text-lg font-normal text-brand-ink">{a.code}</span>
                                </div>
                                <p className="mt-2 text-sm text-brand-ink/70">{a.tag}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Filtres */}
            <section className="sticky top-[92px] z-30 border-b border-brand-line bg-white/90 backdrop-blur">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-6 py-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                        <Filter className="h-4 w-4 text-brand-orange" /> Filtrer :
                    </div>

                    <div className="flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2">
                        <label htmlFor="cursus-filter" className="text-xs uppercase tracking-wider text-brand-smoke">Cursus</label>
                        <select
                            id="cursus-filter"
                            data-testid="filter-cursus"
                            value={filters.cursus}
                            onChange={(e) => setFilters((f) => ({ ...f, cursus: e.target.value }))}
                            className="bg-transparent text-sm font-medium text-brand-ink outline-none"
                        >
                            <option value="">Tous</option>
                            {cursus.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2">
                        <label htmlFor="city-filter" className="text-xs uppercase tracking-wider text-brand-smoke">Ville</label>
                        <select
                            id="city-filter"
                            data-testid="filter-city"
                            value={filters.city}
                            onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                            className="bg-transparent text-sm font-medium text-brand-ink outline-none"
                        >
                            <option value="">Toutes</option>
                            {cities.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    {hasFilters && (
                        <button
                            type="button"
                            data-testid="filter-reset"
                            onClick={() => setFilters({ cursus: "", city: "" })}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-smoke hover:text-brand-orange"
                        >
                            <X className="h-4 w-4" /> Réinitialiser
                        </button>
                    )}

                    <div className="ml-auto text-sm text-brand-smoke">
                        {filteredSessions.length} session{filteredSessions.length > 1 ? "s" : ""}
                    </div>
                </div>
            </section>

            {/* Liste */}
            <section className="bg-white py-20 md:py-24">
                <div className="mx-auto max-w-7xl px-6">
                    {loading ? (
                        <div className="text-center text-brand-smoke">Chargement…</div>
                    ) : filteredSessions.length === 0 ? (
                        <div className="rounded-3xl border border-brand-line bg-brand-sand p-12 text-center" data-testid="sessions-empty">
                            <h3 className="font-display text-2xl">Aucune session ne correspond à vos critères.</h3>
                            <p className="mt-3 text-brand-ink/70">
                                Essayez de modifier les filtres ou{" "}
                                <Link to="/contact" className="text-brand-orange font-semibold hover:underline">
                                    contactez-nous
                                </Link>
                                .
                            </p>
                        </div>
                    ) : (
                        <StaggerGroup className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {filteredSessions.map((s) => (
                                <StaggerItem key={s.id}>
                                    <div
                                        data-testid={`session-card-${s.id}`}
                                        className="group flex h-full flex-col rounded-3xl border border-brand-line bg-white p-8 shadow-premium transition-all hover:-translate-y-1 hover:shadow-premium-lg"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                                <CalendarDays className="h-3.5 w-3.5" /> {formatDate(s.start_date)}
                                            </div>
                                            <UrgencyBadge
                                                s={s}
                                                lastOfYear={lastOfYearMap[`${s.formation_slug}|${new Date(s.start_date).getFullYear()}`] === s.id}
                                            />
                                        </div>
                                        <h3 className="mt-4 font-display text-2xl leading-tight text-brand-ink">
                                            {s.formation_name}
                                        </h3>
                                        <div className="mt-5 flex-1 space-y-2.5 text-sm text-brand-ink/75">
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-4 w-4 text-brand-orange" />
                                                {s.city} · {s.venue_name}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Users className="h-4 w-4 text-brand-orange" />
                                                {s.available_seats} / {s.total_seats} places disponibles
                                            </div>
                                            <div className="text-brand-smoke">Formateur · {s.trainer_name}</div>
                                            <div className="pt-2">
                                                <span className="inline-flex items-center rounded-full border border-brand-orange/25 bg-brand-orange/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-orange" data-testid={`session-eligible-${s.id}`}>
                                                    Formation éligible FIFPL ou DPC
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-8 flex items-center justify-between border-t border-brand-line pt-6">
                                            <div className="font-display text-2xl text-brand-ink">390 €</div>
                                            {s.available_seats > 0 ? (
                                                <Link
                                                    to={`/inscription/${s.id}`}
                                                    data-testid={`session-cta-${s.id}`}
                                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                                                >
                                                    S'inscrire
                                                    <ArrowRight className="h-4 w-4" />
                                                </Link>
                                            ) : (
                                                <span
                                                    data-testid={`session-cta-disabled-${s.id}`}
                                                    className="inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-brand-line px-5 py-2.5 text-sm font-semibold text-brand-smoke"
                                                >
                                                    Complète
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </StaggerItem>
                            ))}
                        </StaggerGroup>
                    )}
                </div>
            </section>
        </div>
    );
}
